import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';

// Map Supabase error messages to user-friendly messages
const getFriendlyError = (error) => {
  const msg = error?.message?.toLowerCase() || '';
  if (msg.includes('invalid api key')) return 'Service configuration error. Please contact support.';
  if (msg.includes('user already registered')) return 'An account with this email already exists. Please sign in instead.';
  if (msg.includes('password') && msg.includes('least')) return 'Password is too short. Please use at least 6 characters.';
  if (msg.includes('too many requests') || msg.includes('rate limit')) return 'Too many attempts. Please wait a moment and try again.';
  if (msg.includes('network') || msg.includes('fetch')) return 'Network error. Please check your internet connection.';
  if (msg.includes('invalid email') || msg.includes('valid email')) return 'Please enter a valid email address.';
  if (msg.includes('signup is disabled')) return 'Registration is currently disabled. Please contact support.';
  if (msg.includes('weak password')) return 'Password is too weak. Use a mix of letters, numbers, and symbols.';
  return error?.message || 'Something went wrong. Please try again.';
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const { signUpWithEmail, signInWithGoogle } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match. Please re-enter your password.');
      return;
    }

    if (formData.password.length < 6) {
      toast.warning('Password must be at least 6 characters long.');
      return;
    }

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      toast.warning('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    const { data, error } = await signUpWithEmail(formData.email, formData.password, {
      full_name: `${formData.firstName} ${formData.lastName}`,
      first_name: formData.firstName,
      last_name: formData.lastName,
      phone: formData.phone,
    });
    setIsSubmitting(false);

    if (error) {
      toast.error('Registration Failed', {
        description: getFriendlyError(error),
      });
      return;
    }

    // Check if email confirmation is required
    if (data?.user?.identities?.length === 0) {
      toast.error('Email Already Registered', {
        description: 'An account with this email already exists. Please sign in instead.',
      });
      return;
    }

    if (data?.user && !data.session) {
      // Email confirmation required
      toast.success('Almost There!', {
        description: 'A confirmation link has been sent to your email. Please verify to complete registration.',
        duration: 6000,
      });
      setTimeout(() => navigate('/login'), 3000);
    } else {
      // Auto-confirmed (if email confirmations are disabled in Supabase)
      toast.success('Welcome to OlivOak Villa!', {
        description: 'Your account has been created successfully.',
      });
      setTimeout(() => navigate('/dashboard'), 1000);
    }
  };

  const handleGoogleSignUp = async () => {
    const { error } = await signInWithGoogle();
    if (error) {
      toast.error('Google Sign Up Failed', {
        description: getFriendlyError(error),
      });
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: 'var(--bg-primary)', paddingTop: '80px', paddingBottom: '80px' }}
    >
      <div
        className="w-full max-w-md p-8"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '1px solid var(--border-light)'
        }}
      >
        <div className="text-center mb-8">
          <h1 className="heading-1 mb-2">Create Account</h1>
          <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
            Join OlivOak Villa
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                style={{ borderRadius: 0 }}
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                style={{ borderRadius: 0 }}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              style={{ borderRadius: 0 }}
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+263..."
              required
              style={{ borderRadius: 0 }}
            />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="At least 6 characters"
                required
                style={{ borderRadius: 0, paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--text-light)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
              style={{ borderRadius: 0 }}
            />
          </div>

          <Button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
            {isSubmitting ? (
              <><Loader2 className="animate-spin mr-2" size={16} /> Creating Account...</>
            ) : (
              'Create Account'
            )}
          </Button>
        </form>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t" style={{ borderColor: 'var(--border-light)' }}></div>
          </div>
          <div className="relative flex justify-center">
            <span className="px-4 body-small" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-light)' }}>
              or sign up with
            </span>
          </div>
        </div>

        {/* Google Sign Up */}
        <button
          onClick={handleGoogleSignUp}
          className="w-full flex items-center justify-center gap-3 px-6 py-3 border transition-all hover:bg-[var(--bg-subtle)]"
          style={{
            borderColor: 'var(--border-medium)',
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            fontFamily: "'Inter', sans-serif",
            cursor: 'pointer'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Sign up with Google
        </button>

        <div className="mt-6 text-center">
          <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
            Already have an account?{' '}
            <Link
              to="/login"
              className="btn-secondary inline"
              style={{ textDecoration: 'underline' }}
            >
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
